"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import Letstalk from "../header/LetsTalkS1";
export interface VideoHeroProps {
  posterSrc: string;
  videoSrc: string;
  overlayLabel?: string;
  overlayDetail?: string;
  heading: string;
  description: string;
  ctaLabel: string;
  onCtaClick?: () => void;
  className?: string;
}

export const VideoHero = ({
  posterSrc,
  videoSrc,
  overlayLabel = "SINCE",
  overlayDetail,
  heading,
  description,
  ctaLabel,
  onCtaClick,
  className,
}: VideoHeroProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const dialogVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    const preview = previewVideoRef.current;
    if (!preview) return;

    preview.muted = true;
    preview.defaultMuted = true;
    preview.playsInline = true;
    preview.setAttribute("playsinline", "true");
    preview.setAttribute("webkit-playsinline", "true");
    preview.setAttribute("muted", "true");
    preview.setAttribute("autoplay", "true");

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const shouldAutoplay = !isOpen && (!mediaQuery.matches || window.innerWidth < 768);

    const startPlayback = () => {
      if (shouldAutoplay) {
        void preview.play().catch(() => {});
      }
    };

    if (isOpen) {
      preview.pause();
      return;
    }

    if (shouldAutoplay) {
      preview.addEventListener("canplay", startPlayback, { once: true });
      preview.addEventListener("loadeddata", startPlayback, { once: true });
      startPlayback();
    } else {
      preview.pause();
    }

    return () => {
      preview.removeEventListener("canplay", startPlayback);
      preview.removeEventListener("loadeddata", startPlayback);
    };
  }, [isOpen]);

  useEffect(() => {
    const video = dialogVideoRef.current;
    if (!video) return;

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const shouldAutoplay = !mediaQuery.matches && isOpen;

    if (shouldAutoplay) {
      video.currentTime = 0;
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isOpen]);

  return (
    <div
      className={cn(
        "grid grid-cols-1 items-center rounded-b-3xl gap-10 lg:grid-cols-3 bg-background lg:gap-20 p-6 lg:p-20 2xl:p-24",
        className,
      )}
    >
      <div className="col-span-1 lg:col-span-2">
        {/* Thumbnail / play trigger */}
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          aria-haspopup="dialog"
          aria-label={`Play video: ${heading}`}
          className="group relative aspect-[16/9.4] w-full overflow-hidden rounded-3xl text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F27A5E]/40"
        >
          <video
            ref={previewVideoRef}
            src={videoSrc}
            poster={posterSrc}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />

          {overlayLabel && (
            <span className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 select-none text-6xl font-black leading-none tracking-tight text-white drop-shadow-sm sm:text-7xl">
              {overlayLabel}
              {overlayDetail && (
                <span className="ml-2 text-white/0">{overlayDetail}</span>
              )}
            </span>
          )}

          <span className="absolute right-10 flex items-center justify-center">
            <span className="absolute h-20 w-20 sm:h-28 sm:w-28 md:h-40 md:w-40 rounded-full bg-primary transition-transform duration-300 group-hover:scale-110" />
            <span className="relative flex h-14 w-14 sm:h-20 sm:w-20 md:h-28 md:w-28 items-center justify-center rounded-full">
              <span className="text-xs md:text-base text-center font-bold uppercase tracking-wide text-white">
                Play
                <br />
                Showreel
              </span>
            </span>
          </span>
        </button>
      </div>

      {/* Text content */}
      <div className="col-span-1 w-full space-y-8">
        <h2 className="w-full text-3xl lg:text-4xl 2xl:text-5xl font-extrabold leading-tight text-neutral-900 ">
          {heading}
        </h2>
        <p className="w-full 2xl:text-xl text-neutral-600">
          {description}
        </p>

      <Letstalk/>
      </div>

      {/* Popup */}
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${heading} video`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 sm:p-8"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsOpen(false);
          }}
        >
          <div className="relative w-full max-w-7xl">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="absolute -top-12 right-0 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-secondary sm:top-0 sm:right-0 sm:translate-x-0 sm:-translate-y-[calc(100%+12px)]"
            >
              Close
              <span aria-hidden>✕</span>
            </button>

            <video
              ref={dialogVideoRef}
              src={videoSrc}
              poster={posterSrc}
              controls
              playsInline
              preload="metadata"
              className="max-h-[80vh] w-full rounded-lg bg-black"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoHero;